import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TaskCategory = 'patient_care' | 'administrative' | 'follow_up' | 'documentation';

export interface PatientTask {
  id: string;
  clinic_id: string;
  patient_id: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  category: TaskCategory;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_by: string | null;
  assigned_by_name: string | null;
  due_date: string | null;
  created_at: string;
  updated_at: string;
}

export const patientTasksQueryKey = (patientId: string | null) =>
  ['tasks', 'patient', patientId] as const;

export function usePatientTasks(patientId: string | null | undefined) {
  return useQuery({
    queryKey: patientTasksQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientTask[]> => {
      if (!patientId) return [];
      
      const { data, error } = await supabase
        .from('tasks')
        .select(
          'id, clinic_id, patient_id, title, description, status, priority, category, ' +
          'assigned_to, assigned_by, due_date, created_at, updated_at, ' +
          'assignee:users!tasks_assigned_to_fkey(name,email), ' +
          'assigner:users!tasks_assigned_by_fkey(name,email)'
        )
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      type Joined = {
        id: string;
        clinic_id: string;
        patient_id: string;
        title: string;
        description: string | null;
        status: TaskStatus;
        priority: TaskPriority;
        category: TaskCategory;
        assigned_to: string | null;
        assigned_by: string | null;
        due_date: string | null;
        created_at: string;
        updated_at: string;
        assignee: { name: string | null; email: string } | null;
        assigner: { name: string | null; email: string } | null;
      };

      const tasks = ((data ?? []) as unknown as Joined[]).map(
        (r): PatientTask => ({
          id: r.id,
          clinic_id: r.clinic_id,
          patient_id: r.patient_id,
          title: r.title,
          description: r.description,
          status: r.status,
          priority: r.priority,
          category: r.category,
          assigned_to: r.assigned_to,
          assigned_to_name: r.assignee?.name ?? r.assignee?.email ?? null,
          assigned_by: r.assigned_by,
          assigned_by_name: r.assigner?.name ?? r.assigner?.email ?? null,
          due_date: r.due_date,
          created_at: r.created_at,
          updated_at: r.updated_at,
        })
      );
      
      return tasks;
    },
    staleTime: 15_000,
  });
}

export interface CreateTaskInput {
  clinic_id: string;
  patient_id: string;
  title: string;
  description?: string | null;
  priority?: TaskPriority;
  category?: TaskCategory;
  assigned_to?: string | null;
  due_date?: string | null;
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: CreateTaskInput) => {
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          clinic_id: input.clinic_id,
          patient_id: input.patient_id,
          title: input.title,
          description: input.description ?? null,
          priority: input.priority ?? 'medium',
          category: input.category ?? 'patient_care',
          assigned_to: input.assigned_to ?? null,
          assigned_by: (await supabase.auth.getUser()).data.user?.id ?? null,
          due_date: input.due_date ?? null,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: patientTasksQueryKey(variables.patient_id),
      });
    },
  });
}

export interface UpdateTaskInput {
  task_id: string;
  patient_id: string;
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  category?: TaskCategory;
  assigned_to?: string | null;
  due_date?: string | null;
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: UpdateTaskInput) => {
      const { task_id, patient_id, ...updates } = input;
      
      const { data, error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', task_id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: patientTasksQueryKey(variables.patient_id),
      });
    },
  });
}

export interface DeleteTaskInput {
  task_id: string;
  patient_id: string;
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: DeleteTaskInput) => {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', input.task_id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: patientTasksQueryKey(variables.patient_id),
      });
    },
  });
}
